import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { printTemplateProblems } from '@optiflow/shared';
import {
  PRINT_FOOTER_MAX_CHARS,
  PRINT_MARGIN_MAX_MM,
  PRINT_PAPER,
  PRINT_PAPER_SIZES_BY_DOCUMENT,
  PrintAlignment,
  PrintDensity,
  PrintFontScale,
  PrintLogoSize,
  PrintQrPlacement,
  PrintQrSize,
  SupportedLocale,
  type BranchPrintTemplateView,
  type PrintDocumentType,
  type PrintedBusinessIdentity,
  type PrintTemplate,
  type PrintTemplateField,
  type PrintTemplateOverride,
  type PrintTemplateView,
} from '@optiflow/types';
import i18n, { directionFor } from '@/i18n';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';
import { useBusinessProfileQuery } from '@/modules/company/businessProfile.api';
import { PrintDocument } from '../print/PrintDocument';
import { printLayout } from '../print/printLayout';
import {
  useBranchPrintTemplatesQuery,
  usePrintTemplatesQuery,
  useResetBranchOverrideMutation,
  useSaveBranchOverrideMutation,
  useSavePrintTemplateMutation,
} from './printTemplates.api';
import { SAMPLE_IDENTITY, sampleDocument } from './sampleDocument';

const DOCUMENT_TYPES: PrintDocumentType[] = ['RECEIPT', 'INVOICE'];
/** Millimetres to CSS pixels at the browser's 96 dpi, for fitting the true-size preview to its card. */
const PX_PER_MM = 96 / 25.4;
const TENANT_SCOPE = 'TENANT';

/** What the branding controls know about the tenant's current profile: new documents freeze exactly this at issuance. */
interface BrandingState {
  identity: PrintedBusinessIdentity;
  hasLogo: boolean;
  hasTaxNumber: boolean;
  hasLegalName: boolean;
}

/**
 * Settings › Printing: how receipts and invoices look, for the tenant or for one branch.
 *
 * Structured choices only, per document type. A branch either uses the tenant template or overrides some
 * of its fields; everything it does not override keeps following the tenant. Layout is presentation and
 * applies to every print and reprint; who issued a document is frozen with the document and never comes
 * from here. The preview is the real renderer drawing a fixed sample with the tenant's own current
 * business profile, so it never reads a customer, an order or a verification token.
 */
export function PrintSettingsPage() {
  const { t } = useTranslation();
  const { branches } = useBranch();
  const profile = useBusinessProfileQuery();
  const [scope, setScope] = useState<string>(TENANT_SCOPE);
  const [active, setActive] = useState<PrintDocumentType>('RECEIPT');
  const scopeId = useId();

  const branding: BrandingState = useMemo(() => {
    const data = profile.data;
    if (!data) {
      return {
        identity: SAMPLE_IDENTITY,
        hasLogo: false,
        hasTaxNumber: false,
        hasLegalName: false,
      };
    }
    return {
      identity: {
        source: 'SNAPSHOT',
        displayName: data.resolvedDisplayName,
        legalName: data.legalName,
        taxRegistrationNumber: data.taxRegistrationNumber,
        logo: data.logo
          ? { url: data.logo.url, width: data.logo.width, height: data.logo.height }
          : null,
      },
      hasLogo: data.logo !== null,
      hasTaxNumber: data.taxRegistrationNumber !== null,
      hasLegalName: data.legalName !== null,
    };
  }, [profile.data]);

  return (
    <div className="space-y-6">
      <PageHeader title={t('printSettings.title')} subtitle={t('printSettings.subtitle')} />

      {branches.length > 0 ? (
        <div className="max-w-sm">
          <label htmlFor={scopeId} className="field-label">
            {t('printSettings.scope.label')}
          </label>
          <Select
            id={scopeId}
            value={scope}
            data-testid="print-scope"
            options={[
              { value: TENANT_SCOPE, label: t('printSettings.scope.tenant') },
              ...branches.map((branch) => ({
                value: branch.id,
                label: t('printSettings.scope.branch', { name: branch.name }),
              })),
            ]}
            onChange={(event) => setScope(event.target.value)}
          />
        </div>
      ) : null}

      <div
        role="tablist"
        aria-label={t('printSettings.documentType')}
        className="flex flex-wrap gap-2"
      >
        {DOCUMENT_TYPES.map((type) => (
          <Button
            key={type}
            role="tab"
            aria-selected={type === active}
            variant={type === active ? 'primary' : 'secondary'}
            onClick={() => setActive(type)}
          >
            {t(`printSettings.documents.${type}`)}
          </Button>
        ))}
      </div>

      {scope === TENANT_SCOPE ? (
        <TenantEditor active={active} branding={branding} />
      ) : (
        <BranchEditor key={scope} branchId={scope} active={active} branding={branding} />
      )}
    </div>
  );
}

function useErrorText(error: unknown, isError: boolean, fallback: string): string | null {
  const { t } = useTranslation();
  if (!isError) return null;
  const code = apiErrorCode(error);
  return code && i18n.exists(`apiErrors.${code}`)
    ? t(`apiErrors.${code}`)
    : (apiErrorMessage(error) ?? fallback);
}

function ProblemList({
  documentType,
  draft,
}: {
  documentType: PrintDocumentType;
  draft: PrintTemplate;
}) {
  const { t } = useTranslation();
  const problems = printTemplateProblems(documentType, draft);
  if (problems.length === 0) return null;
  return (
    <Alert tone="warning">
      <ul className="list-inside list-disc" data-testid="print-problems">
        {problems.map((problem) => (
          <li key={`${problem.field}-${problem.code}`}>
            {t(`printSettings.problems.${problem.code}`)}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/** The tenant template for each document type, falling back to the system default. */
function TenantEditor({
  active,
  branding,
}: {
  active: PrintDocumentType;
  branding: BrandingState;
}) {
  const { t } = useTranslation();
  const templates = usePrintTemplatesQuery();
  const save = useSavePrintTemplateMutation();
  const [drafts, setDrafts] = useState<Record<PrintDocumentType, PrintTemplate> | null>(null);
  const [saved, setSaved] = useState<PrintDocumentType | null>(null);
  const errorText = useErrorText(save.error, save.isError, t('printSettings.saveError'));

  const views = useMemo(() => {
    const byType = new Map<PrintDocumentType, PrintTemplateView>();
    for (const view of templates.data ?? []) byType.set(view.documentType, view);
    return byType;
  }, [templates.data]);

  useEffect(() => {
    if (!templates.data) return;
    setDrafts({
      RECEIPT: { ...(views.get('RECEIPT')?.template as PrintTemplate) },
      INVOICE: { ...(views.get('INVOICE')?.template as PrintTemplate) },
    });
  }, [templates.data, views]);

  useEffect(() => {
    setSaved(null);
  }, [active]);

  if (templates.isPending || !drafts) return <LoadingState />;
  if (templates.isError) {
    return (
      <Alert tone="error">{apiErrorMessage(templates.error) ?? t('printSettings.loadError')}</Alert>
    );
  }

  const draft = drafts[active];
  const view = views.get(active);
  const problems = printTemplateProblems(active, draft);
  const unchanged = view !== undefined && !view.isDefault && sameTemplate(view.template, draft);

  const change: Change = (field, value) => {
    setSaved(null);
    save.reset();
    setDrafts((current) =>
      current ? { ...current, [active]: { ...current[active], [field]: value } } : current,
    );
  };

  const submit = () => {
    if (!view || problems.length > 0) return;
    save.mutate(
      { documentType: active, template: draft, version: view.version },
      { onSuccess: () => setSaved(active) },
    );
  };

  return (
    <>
      {view?.isDefault ? <Alert tone="info">{t('printSettings.usingDefault')}</Alert> : null}
      {saved === active ? <Alert tone="success">{t('printSettings.saved')}</Alert> : null}
      {errorText ? <Alert tone="error">{errorText}</Alert> : null}
      <ProblemList documentType={active} draft={draft} />
      <TemplateForm documentType={active} draft={draft} change={change} branding={branding}>
        <Button
          onClick={submit}
          isLoading={save.isPending}
          disabled={problems.length > 0 || unchanged}
        >
          {t('printSettings.save', { document: t(`printSettings.documents.${active}`) })}
        </Button>
      </TemplateForm>
    </>
  );
}

/**
 * One branch's print templates: the tenant template with only the fields this branch changes laid over it.
 *
 * A field left equal to the tenant's is not an override, so it keeps following the tenant when the tenant
 * changes; resetting drops every override at once. The branch is named per request and checked by the API.
 */
function BranchEditor({
  branchId,
  active,
  branding,
}: {
  branchId: string;
  active: PrintDocumentType;
  branding: BrandingState;
}) {
  const { t } = useTranslation();
  const views = useBranchPrintTemplatesQuery(branchId);
  const save = useSaveBranchOverrideMutation(branchId);
  const reset = useResetBranchOverrideMutation(branchId);
  const [drafts, setDrafts] = useState<Record<PrintDocumentType, PrintTemplate> | null>(null);
  const [editing, setEditing] = useState<Record<PrintDocumentType, boolean>>({
    RECEIPT: false,
    INVOICE: false,
  });
  const [saved, setSaved] = useState<PrintDocumentType | null>(null);
  const errorText = useErrorText(
    save.error ?? reset.error,
    save.isError || reset.isError,
    t('printSettings.saveError'),
  );

  const byType = useMemo(() => {
    const map = new Map<PrintDocumentType, BranchPrintTemplateView>();
    for (const view of views.data ?? []) map.set(view.documentType, view);
    return map;
  }, [views.data]);

  useEffect(() => {
    if (!views.data) return;
    setDrafts({
      RECEIPT: { ...(byType.get('RECEIPT')?.resolved as PrintTemplate) },
      INVOICE: { ...(byType.get('INVOICE')?.resolved as PrintTemplate) },
    });
    setEditing({
      RECEIPT: (byType.get('RECEIPT')?.version ?? 0) > 0,
      INVOICE: (byType.get('INVOICE')?.version ?? 0) > 0,
    });
  }, [views.data, byType]);

  useEffect(() => {
    setSaved(null);
  }, [active]);

  if (views.isPending || !drafts) return <LoadingState />;
  if (views.isError) {
    return (
      <Alert tone="error">{apiErrorMessage(views.error) ?? t('printSettings.loadError')}</Alert>
    );
  }

  const view = byType.get(active) as BranchPrintTemplateView;
  const draft = drafts[active];
  const overridden = overriddenFields(view.tenantTemplate, draft);
  const stored = Object.keys(view.overrides) as PrintTemplateField[];
  const unchanged =
    overridden.length === stored.length &&
    overridden.every((field) => view.overrides[field] === draft[field]);
  const problems = printTemplateProblems(active, draft);
  const customizing = editing[active];

  const change: Change = (field, value) => {
    setSaved(null);
    save.reset();
    setDrafts((current) =>
      current ? { ...current, [active]: { ...current[active], [field]: value } } : current,
    );
  };

  const submit = () => {
    if (problems.length > 0) return;
    const overrides = Object.fromEntries(
      overridden.map((field) => [field, draft[field]]),
    ) as PrintTemplateOverride;
    save.mutate(
      { documentType: active, overrides, version: view.version },
      { onSuccess: () => setSaved(active) },
    );
  };

  const resetToTenant = () => {
    setSaved(null);
    reset.mutate(active, {
      onSuccess: () => setEditing((current) => ({ ...current, [active]: false })),
    });
  };

  return (
    <>
      <div data-testid="branch-inheritance">
        {!customizing ? (
          <Alert tone="info">
            <p>{t('printSettings.branch.inherits')}</p>
            <Button
              className="mt-3"
              variant="secondary"
              onClick={() => setEditing((current) => ({ ...current, [active]: true }))}
            >
              {t('printSettings.branch.customize')}
            </Button>
          </Alert>
        ) : (
          <Alert tone="info">
            <p>
              {overridden.length === 0
                ? t('printSettings.branch.noOverrides')
                : t('printSettings.branch.overridden', {
                    fields: overridden.map((field) => fieldLabel(t, field)).join(', '),
                  })}
            </p>
            <p className="mt-1">{t('printSettings.branch.restInherits')}</p>
            {view.version > 0 ? (
              <Button
                className="mt-3"
                variant="secondary"
                isLoading={reset.isPending}
                onClick={resetToTenant}
              >
                {t('printSettings.branch.reset')}
              </Button>
            ) : null}
          </Alert>
        )}
      </div>
      {saved === active ? <Alert tone="success">{t('printSettings.saved')}</Alert> : null}
      {errorText ? <Alert tone="error">{errorText}</Alert> : null}
      {customizing ? <ProblemList documentType={active} draft={draft} /> : null}
      <TemplateForm
        documentType={active}
        draft={customizing ? draft : view.resolved}
        change={change}
        branding={branding}
        readOnly={!customizing}
        overridden={customizing ? new Set(overridden) : undefined}
      >
        {customizing ? (
          <Button
            onClick={submit}
            isLoading={save.isPending}
            disabled={problems.length > 0 || unchanged}
          >
            {t('printSettings.branch.save', { document: t(`printSettings.documents.${active}`) })}
          </Button>
        ) : null}
      </TemplateForm>
    </>
  );
}

/** The fields whose value differs from the tenant template: exactly what a branch overrides. */
function overriddenFields(tenant: PrintTemplate, draft: PrintTemplate): PrintTemplateField[] {
  return (Object.keys(tenant) as PrintTemplateField[]).filter(
    (field) => tenant[field] !== draft[field],
  );
}

/** A setting's name as the form labels it. */
function fieldLabel(t: (key: string) => string, field: PrintTemplateField): string {
  if (field.startsWith('margin')) return t(`printSettings.margins.${field}`);
  if (field.startsWith('footer') || field.startsWith('returnPolicy'))
    return t(`printSettings.texts.${field}`);
  return t(`printSettings.${field}`);
}

/** The editing form and its live preview, shared by the tenant and branch editors. */
function TemplateForm({
  documentType,
  draft,
  change,
  branding,
  readOnly = false,
  overridden,
  children,
}: {
  documentType: PrintDocumentType;
  draft: PrintTemplate;
  change: Change;
  branding: BrandingState;
  readOnly?: boolean;
  overridden?: Set<PrintTemplateField>;
  children?: ReactNode;
}) {
  const problemFields = new Set<PrintTemplateField>(
    printTemplateProblems(documentType, draft).map((problem) => problem.field),
  );
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="space-y-6" data-testid="print-settings-form">
        <fieldset
          disabled={readOnly}
          className="min-w-0 space-y-6"
          data-overridden={overridden ? [...overridden].join(' ') : undefined}
        >
          <PaperSection
            documentType={documentType}
            draft={draft}
            change={change}
            problemFields={problemFields}
          />
          <HeaderSection draft={draft} change={change} branding={branding} />
          <ItemsSection draft={draft} change={change} />
          <TotalsSection draft={draft} change={change} />
          <QrSection draft={draft} change={change} problemFields={problemFields} />
          <FooterSection draft={draft} change={change} problemFields={problemFields} />
        </fieldset>
        {children ? <div className="flex justify-end">{children}</div> : null}
      </div>

      <Preview documentType={documentType} template={draft} identity={branding.identity} />
    </div>
  );
}

type Change = <K extends PrintTemplateField>(field: K, value: PrintTemplate[K]) => void;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} />
      <div className="grid gap-4 p-5 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
  invalid,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  invalid?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <Select
        id={id}
        value={value}
        hasError={invalid}
        options={options}
        onChange={(event) => onChange(event.target.value as T)}
      />
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className="text-ink flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        {label}
        {hint ? <span className="text-muted block text-xs">{hint}</span> : null}
      </span>
    </label>
  );
}

function PaperSection({
  documentType,
  draft,
  change,
  problemFields,
}: {
  documentType: PrintDocumentType;
  draft: PrintTemplate;
  change: Change;
  problemFields: Set<PrintTemplateField>;
}) {
  const { t } = useTranslation();
  const margins = ['marginTopMm', 'marginBottomMm', 'marginLeftMm', 'marginRightMm'] as const;
  const layout = printLayout(draft);
  return (
    <Section title={t('printSettings.sections.paper')}>
      <Choice
        label={t('printSettings.paperSize')}
        value={draft.paperSize}
        options={PRINT_PAPER_SIZES_BY_DOCUMENT[documentType].map((size) => ({
          value: size,
          label: t(`printSettings.papers.${size}`),
        }))}
        onChange={(value) => change('paperSize', value)}
        invalid={problemFields.has('paperSize')}
      />
      <Choice
        label={t('printSettings.fontScale')}
        value={draft.fontScale}
        options={Object.values(PrintFontScale).map((scale) => ({
          value: scale,
          label: t(`printSettings.fontScales.${scale}`),
        }))}
        onChange={(value) => change('fontScale', value)}
      />
      {margins.map((field) => (
        <Input
          key={field}
          type="number"
          inputMode="numeric"
          min={0}
          max={PRINT_MARGIN_MAX_MM}
          step={1}
          dir="ltr"
          label={t(`printSettings.margins.${field}`)}
          value={String(draft[field])}
          error={
            problemFields.has(field)
              ? t('printSettings.marginInvalid', { max: PRINT_MARGIN_MAX_MM })
              : undefined
          }
          onChange={(event) => change(field, Number(event.target.value))}
        />
      ))}
      <p className="text-muted text-xs sm:col-span-2" data-testid="usable-width">
        {t('printSettings.usableWidth', {
          width: layout.widthMm,
          paper: PRINT_PAPER[draft.paperSize].widthMm,
        })}
      </p>
    </Section>
  );
}

function HeaderSection({
  draft,
  change,
  branding,
}: {
  draft: PrintTemplate;
  change: Change;
  branding: BrandingState;
}) {
  const { t } = useTranslation();
  return (
    <Section title={t('printSettings.sections.header')}>
      <Choice
        label={t('printSettings.headerAlignment')}
        value={draft.headerAlignment}
        options={Object.values(PrintAlignment).map((value) => ({
          value,
          label: t(`printSettings.alignments.${value}`),
        }))}
        onChange={(value) => change('headerAlignment', value)}
      />
      <div className="space-y-2">
        <Toggle
          label={t('printSettings.showStoreName')}
          checked={draft.showStoreName}
          onChange={(value) => change('showStoreName', value)}
        />
        <Toggle
          label={t('printSettings.showBranchName')}
          checked={draft.showBranchName}
          onChange={(value) => change('showBranchName', value)}
        />
        <Toggle
          label={t('printSettings.showBranchAddress')}
          checked={draft.showBranchAddress}
          onChange={(value) => change('showBranchAddress', value)}
        />
        <Toggle
          label={t('printSettings.showBranchPhone')}
          checked={draft.showBranchPhone}
          onChange={(value) => change('showBranchPhone', value)}
        />
      </div>
      <div className="space-y-2 sm:col-span-2" data-testid="branding-controls">
        <p className="text-muted text-xs">{t('printSettings.brandingFrozen')}</p>
        <Toggle
          label={t('printSettings.showLogo')}
          checked={draft.showLogo}
          disabled={!branding.hasLogo && !draft.showLogo}
          hint={branding.hasLogo ? undefined : t('printSettings.noLogo')}
          onChange={(value) => change('showLogo', value)}
        />
        {draft.showLogo ? (
          <Choice
            label={t('printSettings.logoSize')}
            value={draft.logoSize}
            options={Object.values(PrintLogoSize).map((value) => ({
              value,
              label: t(`printSettings.logoSizes.${value}`),
            }))}
            onChange={(value) => change('logoSize', value)}
          />
        ) : null}
        <Toggle
          label={t('printSettings.showLegalName')}
          checked={draft.showLegalName}
          hint={branding.hasLegalName ? undefined : t('printSettings.noLegalName')}
          onChange={(value) => change('showLegalName', value)}
        />
        <Toggle
          label={t('printSettings.showTaxRegistration')}
          checked={draft.showTaxRegistration}
          hint={branding.hasTaxNumber ? undefined : t('printSettings.noTaxNumber')}
          onChange={(value) => change('showTaxRegistration', value)}
        />
      </div>
    </Section>
  );
}

function ItemsSection({ draft, change }: { draft: PrintTemplate; change: Change }) {
  const { t } = useTranslation();
  const sheet = PRINT_PAPER[draft.paperSize].kind === 'sheet';
  return (
    <Section title={t('printSettings.sections.items')}>
      <Choice
        label={t('printSettings.itemDensity')}
        value={draft.itemDensity}
        options={Object.values(PrintDensity).map((value) => ({
          value,
          label: t(`printSettings.densities.${value}`),
        }))}
        onChange={(value) => change('itemDensity', value)}
      />
      <div className="space-y-2">
        <Toggle
          label={t('printSettings.showSku')}
          checked={draft.showSku}
          onChange={(value) => change('showSku', value)}
        />
        <Toggle
          label={t('printSettings.showQuantity')}
          checked={draft.showQuantity}
          onChange={(value) => change('showQuantity', value)}
        />
        <Toggle
          label={t('printSettings.showUnitPrice')}
          checked={draft.showUnitPrice}
          onChange={(value) => change('showUnitPrice', value)}
        />
        {sheet ? (
          <>
            <Toggle
              label={t('printSettings.showDiscount')}
              checked={draft.showDiscount}
              onChange={(value) => change('showDiscount', value)}
            />
            <Toggle
              label={t('printSettings.showTax')}
              checked={draft.showTax}
              onChange={(value) => change('showTax', value)}
            />
          </>
        ) : null}
        <Toggle
          label={t('printSettings.showLineTotal')}
          checked={draft.showLineTotal}
          onChange={(value) => change('showLineTotal', value)}
        />
        <p className="text-muted text-xs">{t('printSettings.descriptionAlways')}</p>
      </div>
    </Section>
  );
}

function TotalsSection({ draft, change }: { draft: PrintTemplate; change: Change }) {
  const { t } = useTranslation();
  return (
    <Section title={t('printSettings.sections.totals')}>
      <div className="space-y-2 sm:col-span-2">
        <Toggle
          label={t('printSettings.showSubtotal')}
          checked={draft.showSubtotal}
          onChange={(value) => change('showSubtotal', value)}
        />
        <Toggle
          label={t('printSettings.showDiscountTotal')}
          checked={draft.showDiscountTotal}
          onChange={(value) => change('showDiscountTotal', value)}
        />
        <Toggle
          label={t('printSettings.showTaxTotal')}
          hint={t('printSettings.taxAlways')}
          checked={draft.showTaxTotal}
          onChange={(value) => change('showTaxTotal', value)}
        />
        <Toggle
          label={t('printSettings.showPaid')}
          checked={draft.showPaid}
          onChange={(value) => change('showPaid', value)}
        />
        <Toggle
          label={t('printSettings.showOutstanding')}
          checked={draft.showOutstanding}
          onChange={(value) => change('showOutstanding', value)}
        />
        <p className="text-muted text-xs">{t('printSettings.totalAlways')}</p>
      </div>
    </Section>
  );
}

function QrSection({
  draft,
  change,
  problemFields,
}: {
  draft: PrintTemplate;
  change: Change;
  problemFields: Set<PrintTemplateField>;
}) {
  const { t } = useTranslation();
  const kind = PRINT_PAPER[draft.paperSize].kind;
  return (
    <Section title={t('printSettings.sections.qr')}>
      <Choice
        label={t('printSettings.qrSize')}
        value={draft.qrSize}
        options={Object.values(PrintQrSize).map((value) => ({
          value,
          label: t(`printSettings.qrSizes.${value}`, {
            mm: printLayout({ ...draft, qrSize: value }).qrMm,
            kind,
          }),
        }))}
        onChange={(value) => change('qrSize', value)}
        invalid={problemFields.has('qrSize')}
      />
      <Choice
        label={t('printSettings.qrPlacement')}
        value={draft.qrPlacement}
        options={Object.values(PrintQrPlacement).map((value) => ({
          value,
          label: t(`printSettings.qrPlacements.${value}`),
        }))}
        onChange={(value) => change('qrPlacement', value)}
      />
      <p className="text-muted text-xs sm:col-span-2">{t('printSettings.qrAlways')}</p>
    </Section>
  );
}

function FooterSection({
  draft,
  change,
  problemFields,
}: {
  draft: PrintTemplate;
  change: Change;
  problemFields: Set<PrintTemplateField>;
}) {
  const { t } = useTranslation();
  const fields = [
    ['footerEn', 'ltr'],
    ['footerAr', 'rtl'],
    ['returnPolicyEn', 'ltr'],
    ['returnPolicyAr', 'rtl'],
  ] as const;
  return (
    <Section title={t('printSettings.sections.footer')}>
      <div className="sm:col-span-2">
        <Toggle
          label={t('printSettings.showThankYou')}
          checked={draft.showThankYou}
          onChange={(value) => change('showThankYou', value)}
        />
      </div>
      {fields.map(([field, dir]) => (
        <TextArea
          key={field}
          label={t(`printSettings.texts.${field}`)}
          value={draft[field]}
          dir={dir}
          invalid={problemFields.has(field)}
          onChange={(value) => change(field, value)}
        />
      ))}
      <p className="text-muted text-xs sm:col-span-2">{t('printSettings.plainText')}</p>
    </Section>
  );
}

function TextArea({
  label,
  value,
  dir,
  invalid,
  onChange,
}: {
  label: string;
  value: string;
  dir: 'ltr' | 'rtl';
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <textarea
        id={id}
        dir={dir}
        rows={3}
        value={value}
        aria-invalid={invalid ? true : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={
          invalid
            ? 'w-full rounded-lg border border-red-400 bg-white px-3 py-2 text-sm'
            : 'border-line w-full rounded-lg border bg-white px-3 py-2 text-sm'
        }
      />
      <p className="text-muted mt-1 text-xs" dir="ltr">
        {t('printSettings.characters', { used: value.length, max: PRINT_FOOTER_MAX_CHARS })}
      </p>
    </div>
  );
}

/** The real renderer drawing the fixed sample with the unsaved draft, in either language, fitted to its card at true proportions. */
function Preview({
  documentType,
  template,
  identity,
}: {
  documentType: PrintDocumentType;
  template: PrintTemplate;
  identity: PrintedBusinessIdentity;
}) {
  const { t, i18n: pageI18n } = useTranslation();
  const [language, setLanguage] = useState<SupportedLocale>(
    pageI18n.language === SupportedLocale.AR ? SupportedLocale.AR : SupportedLocale.EN,
  );
  const previewI18n = useMemo(() => i18n.cloneInstance({ lng: language }), [language]);
  const document = sampleDocument(documentType, template, identity);
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const layout = printLayout(template);

  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      if (width > 0) setScale(Math.min(1, width / (layout.widthMm * PX_PER_MM + 24)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [layout.widthMm]);

  return (
    <Card>
      <CardHeader
        title={t('printSettings.preview')}
        subtitle={t('printSettings.previewHint')}
        action={
          <div role="group" aria-label={t('printSettings.previewLanguage')} className="flex gap-1">
            {[SupportedLocale.EN, SupportedLocale.AR].map((locale) => (
              <Button
                key={locale}
                size="sm"
                variant={locale === language ? 'primary' : 'secondary'}
                aria-pressed={locale === language}
                onClick={() => setLanguage(locale)}
              >
                {locale === SupportedLocale.EN ? 'English' : 'العربية'}
              </Button>
            ))}
          </div>
        }
        wrapAction
      />
      <div ref={box} className="overflow-hidden bg-slate-200 p-3" data-testid="print-preview">
        <div style={{ zoom: scale }}>
          <I18nextProvider i18n={previewI18n}>
            <div
              dir={directionFor(language)}
              className="print-root mx-auto bg-white shadow-sm"
              data-testid="print-preview-document"
            >
              <PrintDocument document={document} />
            </div>
          </I18nextProvider>
        </div>
      </div>
    </Card>
  );
}

function sameTemplate(a: PrintTemplate, b: PrintTemplate): boolean {
  return (Object.keys(a) as PrintTemplateField[]).every((field) => a[field] === b[field]);
}
